import { CERTIFICATION_COURSES } from '@common/constants/certification.constants'
import { SelectOptionType } from '@common/enums/global.enums'
import { Option } from '@common/types/global.types'
import { AdvancedFiltersValues } from '@common/types/person.types'
import DatePickerInput from '@components/inputs/DatePickerInput'
import SelectInput from '@components/inputs/SelectInput'
import PopIn from '@components/layout/PopIn'
import { Stack, styled } from '@mui/material'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

type AdvancedFiltersPopInProps = {
	onClose: () => void
	onConfirm: (values: AdvancedFiltersValues) => void
	initialValues: AdvancedFiltersValues
}

/**
 * Advanced filters popin
 * @param onClose
 * @param onConfirm
 * @param initialValues
 * @constructor
 */
const AdvancedFiltersPopIn = (
	{
		onClose,
		onConfirm,
		initialValues
	}: AdvancedFiltersPopInProps
) => {
	const { formatMessage } = useIntl()

	const {
		control,
		handleSubmit,
		reset,
		watch
	} = useForm<AdvancedFiltersValues>({
		defaultValues: initialValues
	})

	const start = watch('start')
	const end = watch('end')

	useEffect(() => {
		reset(initialValues)
	}, [initialValues, reset])

	const options: Option[] = Object.values(CERTIFICATION_COURSES).map(value => ({
		type: SelectOptionType.OPTION,
		label: formatMessage({ id: `global.enums.certificationCourse.${value}` }),
		value
	}))

	return (
		<PopIn
			buttons={[
				{
					label: formatMessage({ id: 'global.buttons.apply' }),
					onClick: handleSubmit(onConfirm)
				},
				{
					label: formatMessage({ id: 'global.buttons.clear' }),
					variant: 'tertiary',
					onClick: () => {
						const clearedValues: AdvancedFiltersValues = {
							course: '',
							start: undefined,
							end: undefined
						}
						reset(clearedValues)
						onConfirm(clearedValues)
					}
				}
			]}
			onClose={onClose}
			subtitle={formatMessage({ id: 'dashboard.trainees.filters.advanced.subtitle' })}
			title={formatMessage({ id: 'dashboard.trainees.filters.advanced.title' })}
			isOpen
		>
			<Form>
				<SelectInput
					control={control}
					name="course"
					placeholder={formatMessage({ id: 'dashboard.trainees.filters.advanced.course.select' })}
					label={formatMessage({ id: 'global.fields.course' })}
					options={options}
				/>
				<DatePickerInput
					name="start"
					control={control}
					maxDate={end}
					label={formatMessage({ id: 'dashboard.trainees.filters.advanced.startDate' })}
				/>
				<DatePickerInput
					name="end"
					minDate={start}
					control={control}
					label={formatMessage({ id: 'dashboard.trainees.filters.advanced.endDate' })}
				/>
			</Form>
		</PopIn>
	)
}

const Form = styled(Stack)({
	rowGap: '16px'
})

export default AdvancedFiltersPopIn
